/**
 * Admin Database Console
 * Tabs: MongoDB collections + Supabase Storage buckets
 * Admin-only. Lets you browse any document / file.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sidebar } from '../../components/Sidebar';
import { Database, HardDrive, Activity, RefreshCw, ChevronRight, FileText, Image as ImageIcon, Film } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const AdminDatabase = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState({ kind: 'mongo', name: null }); // {kind: 'mongo'|'bucket', name}
  const [docs, setDocs] = useState(null);
  const [bucketFiles, setBucketFiles] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/database/overview`, { withCredentials: true });
      setOverview(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  const openCollection = async (name) => {
    setActiveView({ kind: 'mongo', name });
    setLoadingDetail(true);
    setDocs(null);
    setBucketFiles(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/database/collection/${name}`, {
        params: { limit: 25 },
        withCredentials: true,
      });
      setDocs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openBucket = async (name) => {
    setActiveView({ kind: 'bucket', name });
    setLoadingDetail(true);
    setDocs(null);
    setBucketFiles(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/database/bucket/${name}/files`, { withCredentials: true });
      setBucketFiles(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-20">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  const mongo = overview?.mongodb;
  const supa = overview?.supabase;
  const totalFiles = supa?.buckets ? Object.values(supa.buckets).reduce((a, b) => a + (b.files || 0), 0) : 0;
  const totalBytes = supa?.buckets ? Object.values(supa.buckets).reduce((a, b) => a + (b.bytes || 0), 0) : 0;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="admin-database-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-2">Infrastructure.</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500">Database Console · MongoDB + Supabase</p>
            </div>
            <button
              onClick={fetchOverview}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-black/[0.10] dark:border-white/10 rounded-lg text-sm text-zinc-300 transition-colors"
              data-testid="db-refresh-button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Health cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.0 }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#00C972]/10 blur-3xl" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#00C972]/15 border border-[#00C972]/30 flex items-center justify-center"><Database size={18} className="text-[#00C972]" /></div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B] dark:text-zinc-500">MongoDB</div>
                  <div className="text-sm text-[#0F0F0F] dark:text-white font-medium">{mongo?.database}</div>
                </div>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-[#00C972]/20 text-[#00C972] text-[10px] font-mono uppercase tracking-wider">healthy</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Version</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{mongo?.version}</div></div>
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Colls</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{Object.keys(mongo?.collections || {}).length}</div></div>
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Docs</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{mongo?.total_documents}</div></div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#FF451A]/15 blur-3xl" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#FF451A]/15 border border-[#FF451A]/30 flex items-center justify-center"><HardDrive size={18} className="text-[#FF451A]" /></div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B] dark:text-zinc-500">Supabase Storage</div>
                  <div className="text-sm text-[#0F0F0F] dark:text-white font-medium">{supa?.url || 'not configured'}</div>
                </div>
                <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${supa?.status === 'healthy' ? 'bg-[#00C972]/20 text-[#00C972]' : 'bg-[#FFB800]/20 text-[#FFB800]'}`}>
                  {supa?.status}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Buckets</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{Object.keys(supa?.buckets || {}).filter((k) => !k.startsWith('_')).length}</div></div>
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Files</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{totalFiles}</div></div>
                <div><div className="font-mono text-[9px] uppercase text-[#A1A1AA] dark:text-zinc-600">Storage</div><div className="text-[#0F0F0F] dark:text-white font-mono text-sm">{formatBytes(totalBytes)}</div></div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-zinc-700/40 blur-3xl" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-zinc-800 border border-black/[0.10] dark:border-white/10 flex items-center justify-center"><Activity size={18} className="text-[#6B6B6B] dark:text-zinc-400" /></div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#6B6B6B] dark:text-zinc-500">Redis Cache</div>
                  <div className="text-sm text-[#0F0F0F] dark:text-white font-medium">phase C — not installed</div>
                </div>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-zinc-700/40 text-[#6B6B6B] dark:text-zinc-400 text-[10px] font-mono uppercase tracking-wider">pending</span>
              </div>
              <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">Will add caching for hot queries (search, dashboard) and event bus.</div>
            </motion.div>
          </div>

          {/* Browser */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
            {/* Left list */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-black/[0.08] dark:border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">MongoDB Collections</div>
                <div className="max-h-80 overflow-y-auto">
                  {Object.entries(mongo?.collections || {}).map(([name, count]) => {
                    const active = activeView.kind === 'mongo' && activeView.name === name;
                    return (
                      <button key={name} onClick={() => openCollection(name)} className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-white/[0.04] transition-colors ${active ? 'bg-[#FF451A]/10 border-l-2 border-[#FF451A]' : 'border-l-2 border-transparent'}`} data-testid={`mongo-collection-${name}`}>
                        <span className="text-[#0F0F0F] dark:text-white truncate">{name}</span>
                        <span className="font-mono text-xs text-[#6B6B6B] dark:text-zinc-500">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-black/[0.08] dark:border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">Supabase Buckets</div>
                <div>
                  {Object.entries(supa?.buckets || {}).filter(([k]) => !k.startsWith('_')).map(([name, info]) => {
                    const active = activeView.kind === 'bucket' && activeView.name === name;
                    return (
                      <button key={name} onClick={() => openBucket(name)} className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-white/[0.04] transition-colors ${active ? 'bg-[#FF451A]/10 border-l-2 border-[#FF451A]' : 'border-l-2 border-transparent'}`} data-testid={`supabase-bucket-${name}`}>
                        <span className="text-[#0F0F0F] dark:text-white truncate flex items-center gap-2">
                          {name}
                          {info.public && <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 bg-[#00C972]/15 text-[#00C972] rounded">PUBLIC</span>}
                        </span>
                        <span className="font-mono text-xs text-[#6B6B6B] dark:text-zinc-500">{info.files ?? '—'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right detail */}
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden min-h-[400px]">
              {!activeView.name ? (
                <div className="p-12 text-center text-[#6B6B6B] dark:text-zinc-500">
                  <ChevronRight size={24} className="mx-auto mb-2 opacity-60" />
                  Select a collection or bucket on the left to inspect.
                </div>
              ) : loadingDetail ? (
                <div className="p-12 text-center">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#FF451A] border-r-transparent"></div>
                </div>
              ) : activeView.kind === 'mongo' && docs ? (
                <CollectionView name={activeView.name} data={docs} />
              ) : activeView.kind === 'bucket' && bucketFiles ? (
                <BucketView name={activeView.name} data={bucketFiles} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CollectionView = ({ name, data }) => (
  <div>
    <div className="px-5 py-4 border-b border-black/[0.08] dark:border-white/5 flex items-center justify-between sticky top-0 bg-[#0F0F0F]/95 backdrop-blur-sm z-10">
      <div>
        <h3 className="font-display text-2xl text-[#0F0F0F] dark:text-white tracking-tight">{name}</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">showing {data.documents.length} of {data.total}</p>
      </div>
    </div>
    <div className="divide-y divide-white/5">
      {data.documents.map((doc) => (
        <details key={doc._id} className="group">
          <summary className="cursor-pointer px-5 py-3 hover:bg-white/[0.03] transition-colors flex items-center gap-3" data-testid="db-doc-row">
            <ChevronRight size={14} className="text-[#6B6B6B] dark:text-zinc-500 group-open:rotate-90 transition-transform" />
            <span className="font-mono text-xs text-[#6B6B6B] dark:text-zinc-500 truncate">{doc._id}</span>
            <span className="text-sm text-[#0F0F0F] dark:text-white truncate flex-1">{doc.name || doc.email || doc.message || doc.invoice_number || doc.action || doc.description || '—'}</span>
          </summary>
          <pre className="px-5 py-3 bg-black/30 text-xs font-mono text-zinc-300 overflow-x-auto border-t border-black/[0.08] dark:border-white/5">{JSON.stringify(doc, null, 2)}</pre>
        </details>
      ))}
    </div>
  </div>
);

const ICON_FOR_MIME = (mime) => {
  if (!mime) return FileText;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.startsWith('video/')) return Film;
  return FileText;
};

const BucketView = ({ name, data }) => {
  const files = (data.files || []).filter((f) => f.name);
  return (
    <div>
      <div className="px-5 py-4 border-b border-black/[0.08] dark:border-white/5 sticky top-0 bg-[#0F0F0F]/95 backdrop-blur-sm z-10">
        <h3 className="font-display text-2xl text-[#0F0F0F] dark:text-white tracking-tight">{name}</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">{files.length} file{files.length !== 1 ? 's' : ''}</p>
      </div>
      {files.length === 0 ? (
        <div className="p-12 text-center text-[#6B6B6B] dark:text-zinc-500 italic">No files in this bucket yet.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {files.map((f) => {
            const mime = f.metadata?.mimetype;
            const size = f.metadata?.size;
            const Icon = ICON_FOR_MIME(mime);
            return (
              <div key={f.id || f.name} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors" data-testid="bucket-file-row">
                <Icon size={16} className="text-[#FF451A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0F0F0F] dark:text-white truncate font-medium">{f.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">
                    {mime || '—'} · {size ? `${(size / 1024).toFixed(1)} KB` : '—'} · {f.created_at ? new Date(f.created_at).toLocaleString() : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
